import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Select, Switch, Checkbox } from "antd";
import { BellRing, Loader2, Plus, Save } from "lucide-react";
import { ContainerCard } from "../ui/cards";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../common/ToastMsg";
import { DEMO_REMINDER_URL, DEMO_REMINDER_LOG_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { TEXT, joinSpec, splitSpec, templatePositions } from "./LeadWelcomeWhatsAppCard";

const STATUS_STYLE = {
    pending: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    sending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    sent:    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    skipped: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
    failed:  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};
const STATUS_LABEL = { pending: 'Waiting', sending: 'Sending', sent: 'Sent', skipped: 'Not sent', failed: 'Failed' };
const LINK_FIELDS = ['reminder_link', 'reminder_code'];
// Minutes before the demo.
export const OFFSETS = [
    { value: 2880, label: '2 days before' }, { value: 1440, label: '1 day before' },
    { value: 240, label: '4 hours before' }, { value: 120, label: '2 hours before' },
    { value: 60, label: '1 hour before' }, { value: 30, label: '30 minutes before' },
    { value: 15, label: '15 minutes before' }, { value: 10, label: '10 minutes before' },
];
export const offsetLabel = (m) => OFFSETS.find(o => o.value === m)?.label
    || (m % 1440 === 0 ? `${m / 1440} day(s) before` : m % 60 === 0 ? `${m / 60} hour(s) before` : `${m} min before`);
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SLOT_LENGTHS = [15, 30, 45, 60, 90, 120].map(v => ({ value: v, label: `${v} minutes` }));
const NOTICE = [
    { value: 0, label: 'None' }, { value: 30, label: '30 minutes' }, { value: 60, label: '1 hour' },
    { value: 120, label: '2 hours' }, { value: 240, label: '4 hours' }, { value: 1440, label: '1 day' },
];
const LABEL = "text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block";

const fmt = (d) => (d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '');

const formOf = ({ mailboxes, wa_accounts, wa_templates, wa_fields, ...rest }) =>
    ({ ...rest, wa_variables: rest.wa_variables || {} });

/**
 * Settings → Email Templates: remind a lead before their booked demo (by
 * default a day before and 10 minutes before) by email and/or an approved
 * WhatsApp template. Each carries a personal link to the website's demo page,
 * where the lead confirms or moves the demo to a free slot on the company
 * demo calendar set here. Backend: email/demo-reminder/
 * (platform_core/email_integration/demo_reminder.py). Off until switched on.
 */
export default function DemoReminderCard({ templates, onCreateTemplate }) {
    const { fetch, update } = useApiMethods();
    const navigate = useNavigate();
    const [form, setForm] = useState(null);
    const [options, setOptions] = useState({ mailboxes: [], wa_accounts: [], wa_templates: [], wa_fields: [] });
    const [channel, setChannel] = useState('email');
    const [log, setLog] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const take = (data) => {
        setForm(formOf(data));
        setOptions({ mailboxes: data.mailboxes || [], wa_accounts: data.wa_accounts || [],
                     wa_templates: data.wa_templates || [], wa_fields: data.wa_fields || [] });
    };

    useEffect(() => {
        fetch(DEMO_REMINDER_URL).then(cfg => {
            if (cfg?.status === 'success') { take(cfg.data); setError(''); }
            else setError(cfg?.message || 'Could not load the demo reminder settings');
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetch(`${DEMO_REMINDER_LOG_URL}?channel=${channel}`).then(res => {
            if (res?.status === 'success') setLog(res.data || []);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channel]);

    const fieldKeys = options.wa_fields.map(f => f.key);
    const waTemplate = options.wa_templates.find(t => t.id === form?.wa_template_id);
    const positions = useMemo(() => templatePositions(waTemplate), [waTemplate]);

    const save = async (patch = {}) => {
        const vars = Object.fromEntries(positions.map(p => [p, form.wa_variables[p] || '']));
        setSaving(true);
        const res = await update(DEMO_REMINDER_URL, { ...form, wa_variables: vars, ...patch });
        setSaving(false);
        if (res?.status === 'success') {
            take(res.data);
            const which = 'wa_enabled' in patch ? 'WhatsApp' : 'Email';
            const on = patch.is_enabled ?? patch.wa_enabled;
            ToastMsg('success', on === undefined ? 'Saved' : `${which} demo reminder switched ${on ? 'on' : 'off'}`);
        } else {
            ToastMsg('error', res?.message || 'Save failed');
        }
    };

    if (error) {
        return <ContainerCard><p className="text-sm text-red-600 dark:text-red-400">{error}</p></ContainerCard>;
    }
    if (!form) {
        return (
            <ContainerCard>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading demo reminders…
                </div>
            </ContainerCard>
        );
    }

    const set = (key) => (v) => setForm(p => ({ ...p, [key]: v ?? null }));
    const setVar = (pos, part) => setForm(p => {
        const cur = splitSpec(p.wa_variables[pos], fieldKeys);
        return { ...p, wa_variables: { ...p.wa_variables, [pos]: joinSpec({ ...cur, ...part }) } };
    });
    const toggleDay = (d) => setForm(p => ({
        ...p, working_days: p.working_days.includes(d)
            ? p.working_days.filter(x => x !== d) : [...p.working_days, d].sort(),
    }));
    const chosen = templates.find(t => t.id === form.template_id);
    const missingLink = chosen && !`${chosen.subject || ''}${chosen.body_html || ''}`.includes('{{reminder_link}}');
    const accountTemplates = options.wa_templates.filter(
        t => !form.wa_account_id || !t.account_id || t.account_id === form.wa_account_id);
    const fieldOptions = [
        ...options.wa_fields.map(f => ({ value: f.key, label: f.label })),
        { value: TEXT, label: 'Fixed text…' },
    ];
    const offsetOptions = [...OFFSETS, ...form.offsets.filter(m => !OFFSETS.some(o => o.value === m))
        .map(m => ({ value: m, label: offsetLabel(m) }))];

    return (
        <ContainerCard>
            <div className="flex items-start gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-brand-50 dark:bg-brand-900/30 flex items-center justify-center shrink-0">
                    <BellRing className="h-4 w-4 text-brand-600 dark:text-brand-300" />
                </div>
                <div>
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Demo reminder</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        Before a booked demo (Demo Fixed with a date and time), the lead gets a reminder with a personal link.
                        They tap <b>Yes, I'll join</b>, or pick another free time from your demo calendar below; the CRM moves the
                        demo, writes it on the timeline with their note, and tells the owner and the presenter. A reminder whose
                        time has already passed when the demo is booked is not sent.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className={LABEL}>Demo page URL</label>
                    <Input value={form.page_url || ''} placeholder="https://emedhub.in/my-demo/"
                           onChange={(e) => set('page_url')(e.target.value)} />
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">Each lead's code is added to the end.</p>
                </div>
                <div>
                    <label className={LABEL}>When to remind</label>
                    <Select className="w-full" mode="multiple" value={form.offsets} options={offsetOptions}
                            aria-label="When to remind" maxCount={4} onChange={set('offsets')} />
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">Up to four, counted back from the demo time.</p>
                </div>
            </div>

            <section className="mt-5 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Demo calendar</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    The times a lead can move their demo to. A time the presenter already has another demo is not offered,
                    and the lead's own preferred slots are marked and listed first.
                </p>
                <label className={LABEL}>Working days</label>
                <div className="flex flex-wrap gap-1.5 mb-3" role="group" aria-label="Working days">
                    {WEEKDAYS.map((d, i) => (
                        <button key={d} type="button" aria-pressed={form.working_days.includes(i)} onClick={() => toggleDay(i)}
                                className={cn("px-2.5 py-1 rounded-full border text-xs font-medium",
                                    form.working_days.includes(i)
                                        ? "border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-200"
                                        : "border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400")}>
                            {d}
                        </button>
                    ))}
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                    <div>
                        <label className={LABEL} htmlFor="demo-day-start">From</label>
                        <Input id="demo-day-start" type="time" value={form.day_start}
                               onChange={(e) => set('day_start')(e.target.value)} />
                    </div>
                    <div>
                        <label className={LABEL} htmlFor="demo-day-end">Until</label>
                        <Input id="demo-day-end" type="time" value={form.day_end}
                               onChange={(e) => set('day_end')(e.target.value)} />
                    </div>
                    <div>
                        <label className={LABEL}>Slot length</label>
                        <Select className="w-full" value={form.slot_minutes} options={SLOT_LENGTHS}
                                aria-label="Slot length" onChange={set('slot_minutes')} />
                    </div>
                    <div>
                        <label className={LABEL} htmlFor="demo-days-ahead">Days ahead</label>
                        <Input id="demo-days-ahead" type="number" min={1} max={30} value={form.days_ahead}
                               onChange={(e) => set('days_ahead')(Number(e.target.value))} />
                    </div>
                    <div>
                        <label className={LABEL}>Minimum notice</label>
                        <Select className="w-full" value={form.min_notice_minutes} options={NOTICE}
                                aria-label="Minimum notice" onChange={set('min_notice_minutes')} />
                    </div>
                </div>
            </section>

            <div className="mt-5 grid grid-cols-1 xl:grid-cols-2 gap-4">
                <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Email</h3>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600 dark:text-gray-300">{form.is_enabled ? 'On' : 'Off'}</span>
                            <Switch checked={form.is_enabled} loading={saving} aria-label="Email demo reminder"
                                    onChange={(v) => save({ is_enabled: v })} />
                        </div>
                    </div>
                    <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Template</label>
                        {onCreateTemplate && (
                            <button type="button" onClick={onCreateTemplate}
                                    className="flex items-center gap-1 text-xs font-medium text-brand-600 dark:text-brand-300 hover:underline">
                                <Plus className="h-3 w-3" /> New template
                            </button>
                        )}
                    </div>
                    <Select className="w-full" placeholder="Choose a template" allowClear showSearch
                            optionFilterProp="label" value={form.template_id ?? undefined}
                            options={templates.map(t => ({ value: t.id, label: t.name }))}
                            onChange={set('template_id')} />
                    {missingLink ? (
                        <p className="text-[11px] text-red-600 dark:text-red-400 mt-1">
                            This template has no {'{{reminder_link}}'} — add it where the button or link should go.
                        </p>
                    ) : (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                            Put {'{{reminder_link}}'} in the template; {'{{demo_time}}'}, {'{{demo_presenter}}'} and {'{{first_name}}'} work too.
                        </p>
                    )}
                    <label className={cn(LABEL, "mt-3")}>Send from</label>
                    <Select className="w-full" placeholder="Choose a mailbox" allowClear
                            value={form.sender_account_id ?? undefined}
                            options={options.mailboxes.map(m => ({
                                value: m.id, disabled: !m.can_send,
                                label: `${m.email_address}${m.owner ? ` (${m.owner})` : ''}${m.can_send ? '' : ' - SMTP not verified'}`,
                            }))}
                            onChange={set('sender_account_id')} />
                    <Checkbox className="mt-2" checked={form.send_from_owner}
                              onChange={(e) => set('send_from_owner')(e.target.checked)}>
                        <span className="text-xs text-gray-600 dark:text-gray-300">
                            Use the lead owner's own mailbox when they have one
                        </span>
                    </Checkbox>
                </section>

                <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">WhatsApp</h3>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600 dark:text-gray-300">{form.wa_enabled ? 'On' : 'Off'}</span>
                            <Switch checked={form.wa_enabled} loading={saving} aria-label="WhatsApp demo reminder"
                                    onChange={(v) => save({ wa_enabled: v })} />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className={LABEL}>Send from</label>
                            <Select className="w-full" placeholder="WhatsApp account" allowClear
                                    value={form.wa_account_id ?? undefined}
                                    options={options.wa_accounts.map(a => ({
                                        value: a.id, label: `${a.name}${a.phone_number ? ` (${a.phone_number})` : ''}`,
                                    }))}
                                    onChange={set('wa_account_id')} />
                        </div>
                        <div>
                            <label className={LABEL}>Template</label>
                            <Select className="w-full" placeholder="Approved template" allowClear showSearch
                                    optionFilterProp="label" value={form.wa_template_id ?? undefined}
                                    options={accountTemplates.map(t => ({ value: t.id, label: `${t.name} · ${t.category}` }))}
                                    notFoundContent={<span className="text-xs text-gray-500">No approved templates - sync them under Settings → WA Templates</span>}
                                    onChange={set('wa_template_id')} />
                        </div>
                    </div>
                    {waTemplate && (
                        <div className="mt-3 space-y-2">
                            <p className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap rounded-lg bg-green-50/60 dark:bg-green-900/10 border border-green-100 dark:border-green-900/40 p-2.5">
                                {waTemplate.body_text}
                            </p>
                            {positions.map(pos => {
                                const v = splitSpec(form.wa_variables[pos], fieldKeys);
                                return (
                                    <div key={pos} className="flex items-center gap-2">
                                        <span className="w-10 text-xs font-mono text-gray-500">{`{{${pos}}}`}</span>
                                        <Select className="flex-1 min-w-0" placeholder="Fill with…" value={v.field || undefined}
                                                options={fieldOptions} aria-label={`Variable ${pos}`}
                                                onChange={(f) => setVar(pos, { field: f, text: f === TEXT ? v.text : '' })} />
                                        {v.field === TEXT ? (
                                            <Input className="flex-1" value={v.text} placeholder="Text to send"
                                                   onChange={(e) => setVar(pos, { text: e.target.value })} />
                                        ) : ![...LINK_FIELDS, ''].includes(v.field) && (
                                            <Input className="flex-1" value={v.fallback} placeholder="If blank, send…"
                                                   onChange={(e) => setVar(pos, { fallback: e.target.value })} />
                                        )}
                                    </div>
                                );
                            })}
                            <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                For a button whose URL ends in a variable (…/my-demo/{'{{2}}'}) choose <b>Demo reminder code (button)</b>.
                                Use a Utility template so Meta's marketing limits don't hold it back.
                            </p>
                        </div>
                    )}
                </section>
            </div>

            <div className="flex justify-end mt-4">
                <Button onClick={() => save()} disabled={saving} className="text-sm flex items-center gap-2">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                </Button>
            </div>

            <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Recent reminders</h3>
                    <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 p-0.5 text-xs" role="tablist">
                        {[['email', 'Email'], ['whatsapp', 'WhatsApp']].map(([key, label]) => (
                            <button key={key} type="button" role="tab" aria-selected={channel === key}
                                    onClick={() => setChannel(key)}
                                    className={cn("px-2.5 py-1 rounded-md font-medium",
                                        channel === key ? "bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-200"
                                            : "text-gray-500 dark:text-gray-400")}>
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
                {log.length === 0 ? (
                    <p className="text-xs text-gray-400">No demo reminders yet.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left border-b border-gray-200 dark:border-gray-700">
                                    {['Lead', 'Demo', 'Reminder', 'Status', 'To', 'When'].map(h => (
                                        <th key={h} className="pb-2 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                {log.map(r => (
                                    <tr key={r.id}>
                                        <td className="py-2 pr-4">
                                            <button type="button" className="text-brand-600 dark:text-brand-300 hover:underline text-left"
                                                    onClick={() => navigate(`/customers/customerdetails?id=${r.customer_id}`,
                                                        { state: { formEdit: true, row: { id: r.customer_id } } })}>
                                                {r.customer_name || `Lead #${r.customer_id}`}
                                            </button>
                                        </td>
                                        <td className="py-2 pr-4 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">{fmt(r.demo_at)}</td>
                                        <td className="py-2 pr-4 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">{offsetLabel(r.offset_minutes)}</td>
                                        <td className="py-2 pr-4">
                                            <span className={cn("px-2 py-0.5 text-[10px] font-medium rounded-full", STATUS_STYLE[r.status])}>
                                                {STATUS_LABEL[r.status] || r.status}
                                            </span>
                                            {r.reason && <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{r.reason}</span>}
                                        </td>
                                        <td className="py-2 pr-4 text-xs text-gray-600 dark:text-gray-300">{r.sent_to || '—'}</td>
                                        <td className="py-2 text-xs text-gray-500 dark:text-gray-400">{fmt(r.sent_at || r.send_after)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </ContainerCard>
    );
}
